"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { CONSENT_OPEN_EVENT, getConsent, setConsent } from "@/lib/consent";

// Consent banner for optional analytics cookies (Google Analytics).
//
// Shows until the visitor makes a granted/denied choice. Offers a real
// Accept/Decline choice — required because GA sets identifying cookies and we
// operate from the EU (opt-in). Re-opens when the footer "Cookie settings" link
// dispatches CONSENT_OPEN_EVENT, so a prior choice can be changed/withdrawn.
//
// SEO/Web-Vitals notes (unchanged from the notice version):
// - Returns null on first render (server AND first client paint); mounts only
//   after useEffect → identical SSR HTML for crawlers and users, no hydration
//   mismatch.
// - position: fixed at the bottom → excluded from CLS by the Web Vitals spec.
// - Below-the-fold by default → no LCP impact.
const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only when undecided. Legacy "dismissed" reads as null → re-prompt,
    // because that acknowledgment predates analytics and isn't analytics consent.
    if (getConsent() === null) setVisible(true);

    const onOpen = () => setVisible(true);
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  const choose = (value: "granted" | "denied") => {
    setConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
        <p className="text-sm text-muted-foreground flex-1">
          We use essential cookies to keep you signed in and process payments.
          With your permission, we&rsquo;d also like to use analytics cookies
          (Google Analytics) to see how visitors find and use LoyeeCards, and
          marketing cookies (via Google Tag Manager) to measure and promote our
          ads. You can change this anytime via &ldquo;Cookie settings&rdquo; in
          the footer. See our{" "}
          <Link href="/privacy#cookies" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => choose("denied")} size="sm" variant="outline">
            Decline
          </Button>
          <Button onClick={() => choose("granted")} size="sm">
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
